// src/components/reports/KeyMetrics.tsx
import { useState } from "react";
import { useReportsStore } from "@/store/reportsStore";
import { useAuthStore } from "@/store/authStore";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pencil, Trash2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";

interface KeyMetricsProps {
  openAddMetric: boolean;
  setOpenAddMetric: (open: boolean) => void;
}

export default function KeyMetrics({ openAddMetric, setOpenAddMetric }: KeyMetricsProps) {
  const { metrics, addMetric, updateMetric, deleteMetric } = useReportsStore();
  const { user } = useAuthStore();
  const isPavlik = user?.id != null && String(user.id) === "4";

  const [openEdit, setOpenEdit] = useState<string | null>(null);

  const [newName, setNewName] = useState("");
  const [newValue, setNewValue] = useState("0");

  const [editAmountAdd, setEditAmountAdd] = useState("0");
  const [editAmountSubtract, setEditAmountSubtract] = useState("0");

  const currentMetric = metrics.find(m => m.id === openEdit);

  const handleAddMetric = async () => {
    if (!newName.trim()) {
      toast({ title: "Ошибка", description: "Введите название показателя", variant: "destructive" });
      return;
    }
    await addMetric(newName.trim(), Number(newValue) || 0);
    setOpenAddMetric(false);
    setNewName("");
    setNewValue("0");
  };

  const handleUpdate = async (delta: number) => {
    if (!currentMetric) return;
    const newVal = currentMetric.value + delta;
    await updateMetric(currentMetric.id, newVal);
    setEditAmountAdd("0");
    setEditAmountSubtract("0");
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Удалить показатель "${name}"?`)) return;
    try {
      await deleteMetric(id);
    } catch (err) {
      // toast уже обрабатывается в store
    }
  };

  return (
    <>
      <div className="mt-12 md:mt-16">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-300 via-pink-300 to-cyan-300 tracking-tight">
            Ключевые показатели
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-6">
          {metrics.map((metric) => (
            <div
              key={metric.id}
              className="bg-gradient-to-br from-purple-950/80 to-indigo-950/80 p-8 rounded-3xl border border-purple-600/30 shadow-xl hover:shadow-purple-500/30 transition-all group relative flex flex-col"
            >
              {/* Кнопки управления (только для Павлика) */}
              {isPavlik && (
                <div className="absolute top-5 right-5 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setOpenEdit(metric.id)}
                    className="text-purple-400 hover:text-white h-9 w-9"
                  >
                    <Pencil className="h-5 w-5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(metric.id, metric.name)}
                    className="text-red-400 hover:text-red-300 h-9 w-9"
                  >
                    <Trash2 className="h-5 w-5" />
                  </Button>
                </div>
              )}

              {/* Название показателя — СДЕЛАЛИ ЕЩЁ БОЛЬШЕ */}
              <div className="flex-1 flex items-center justify-center text-center mb-6 pt-4">
                <h3 className="text-4xl md:text-5xl font-bold text-purple-100 leading-tight tracking-tight">
                  {metric.name}
                </h3>
              </div>

              {/* Большое значение */}
              <div className="text-center mt-auto">
                <div className="text-8xl md:text-9xl font-black text-cyan-300 drop-shadow-2xl tracking-tighter leading-none">
                  {metric.value.toLocaleString("ru-RU")}
                </div>
              </div>

              {/* Дата обновления */}
              <div className="text-xs text-purple-400 text-center mt-6 font-medium">
                Обновлено: {new Date(metric.updatedAt).toLocaleDateString("ru-RU")}
              </div>
            </div>
          ))}

          {metrics.length === 0 && (
            <div className="col-span-full text-center py-16 text-purple-400 text-lg">
              Пока нет показателей. Добавьте первый с помощью кнопки выше.
            </div>
          )}
        </div>
      </div>

      {/* МОДАЛКА ДОБАВЛЕНИЯ */}
      <Dialog open={openAddMetric} onOpenChange={setOpenAddMetric}>
        <DialogContent className="sm:max-w-[460px] bg-gradient-to-b from-purple-950 to-indigo-950 border-purple-700/50 text-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-purple-100">Новый показатель</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div>
              <Label className="text-purple-200 mb-2 block">Наименование показателя</Label>
              <Input
                placeholder="Например: Обученные ВЦПСФ"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className="bg-purple-900/70 border-purple-600 text-white py-6 text-lg"
              />
            </div>
            <div>
              <Label className="text-purple-200 mb-2 block">Начальное количество</Label>
              <Input
                type="number"
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                className="bg-purple-900/70 border-purple-600 text-white py-6 text-lg"
              />
            </div>
            <Button onClick={handleAddMetric} className="w-full py-6 text-lg bg-emerald-600 hover:bg-emerald-700">
              Добавить показатель
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* МОДАЛКА РЕДАКТИРОВАНИЯ */}
      <Dialog open={!!openEdit} onOpenChange={() => setOpenEdit(null)}>
        <DialogContent className="sm:max-w-[460px] bg-gradient-to-b from-purple-950 to-indigo-950 border-purple-700/50 text-white">
          {currentMetric && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-purple-100">{currentMetric.name}</DialogTitle>
              </DialogHeader>
              <div className="py-6 space-y-8">
                <div className="text-center">
                  <div className="text-6xl font-black text-cyan-300">{currentMetric.value.toLocaleString("ru-RU")}</div>
                  <p className="text-purple-400 mt-2">текущее значение</p>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <Label className="text-emerald-400 mb-3 block text-center">ДОБАВИТЬ</Label>
                    <Input
                      type="number"
                      value={editAmountAdd}
                      onChange={(e) => setEditAmountAdd(e.target.value)}
                      className="bg-emerald-900/30 border-emerald-500 text-white text-center text-3xl py-8"
                    />
                    <Button
                      onClick={() => handleUpdate(Number(editAmountAdd) || 0)}
                      className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700 py-6 text-lg"
                    >
                      Применить +
                    </Button>
                  </div>

                  <div>
                    <Label className="text-red-400 mb-3 block text-center">УБРАТЬ</Label>
                    <Input
                      type="number"
                      value={editAmountSubtract}
                      onChange={(e) => setEditAmountSubtract(e.target.value)}
                      className="bg-red-900/30 border-red-500 text-white text-center text-3xl py-8"
                    />
                    <Button
                      onClick={() => handleUpdate(-(Number(editAmountSubtract) || 0))}
                      className="w-full mt-4 bg-red-600 hover:bg-red-700 py-6 text-lg"
                    >
                      Применить -
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}